/**
 * Public pricing.
 *
 * The honest headline is that a subscription is credit, plus what it unlocks - which is clearer
 * than most usage-based pricing manages, so it leads rather than hiding in a footnote.
 *
 * Three tiers are shown and Max is an enquiry, because a self-serve grid of four invites a
 * comparison exercise before anybody has tried the thing. The figures here are the seeded plans in
 * `alembic/versions/..._initial_schema.py`; `tests/test_pricing_page.py` holds them to it, because a
 * public price that disagrees with what the product charges is the worst kind of wrong.
 */

import { Check } from "lucide-react";
import { Link } from "react-router";

import { buttonVariants } from "@/components/ui/button";
import { useBranding } from "@/lib/useBranding";

const TIERS = [
  {
    name: "Free",
    price: "$0",
    note: "$6 of credit to start",
    body: "Enough for a few real runs, so you can change a sentence and watch the reaction move.",
    features: ["3 studies", "1 audience", "Results kept 30 days", "10-persona runs"],
    cta: { label: "Start free", to: "/login" },
    highlight: false,
  },
  {
    name: "Starter",
    price: "$10",
    note: "per month, all of it credit",
    body: "Full-size runs, and a year of history to compare against.",
    features: ["$10 of credit a month", "10 studies", "30-persona runs", "Results kept a year"],
    cta: { label: "Start free, upgrade later", to: "/login" },
    highlight: false,
  },
  {
    name: "Pro",
    price: "$30",
    note: "per month, all of it credit",
    body: "Everything, including the tools that work backwards from a target.",
    features: [
      "$30 of credit a month",
      "Unlimited studies",
      "Goal seek and holdout testing",
      "3 seats",
      "Buy more credit any time",
    ],
    cta: { label: "Start free, upgrade later", to: "/login" },
    highlight: true,
  },
];

export function PricingPage() {
  const { brand } = useBranding();

  return (
    <div className="relative min-h-dvh bg-black">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 bg-cover bg-center opacity-35"
        style={{ backgroundImage: `url(${brand.backdrop_url})` }}
      />

      <header className="relative z-10 border-b border-white/10 bg-black/60 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/">
            <img src={brand.logo_url} alt={brand.product_name} className="block h-8 w-auto" />
          </Link>
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/examples" className="px-2 py-1 text-muted-foreground hover:text-foreground">
              Examples
            </Link>
            <Link to="/login" className={buttonVariants({ size: "sm" })}>
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-6 py-14">
        <header className="max-w-2xl space-y-3">
          <h1 className="text-3xl font-semibold md:text-4xl">Your subscription is credit</h1>
          <p className="text-slate-300">
            Every pound you pay arrives as credit to spend on runs — nothing is held back as a
            platform fee. The plan decides how much arrives each month and which tools you can
            reach. You see the price of a run before it starts, and unused credit comes back when it
            finishes.
          </p>
        </header>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {TIERS.map((tier) => (
            <section
              key={tier.name}
              className={`flex flex-col rounded-xl border p-6 backdrop-blur-sm ${
                tier.highlight
                  ? "border-[var(--brand-primary,#3987e5)] bg-[rgb(8_11_15/0.85)]"
                  : "border-white/12 bg-[rgb(8_11_15/0.75)]"
              }`}
            >
              <h2 className="font-semibold">{tier.name}</h2>
              <p className="mt-2 text-3xl font-semibold tabular-nums">{tier.price}</p>
              <p className="text-xs text-muted-foreground">{tier.note}</p>
              <p className="mt-3 text-sm text-slate-300">{tier.body}</p>
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-2">
                    <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-[#0ca30c]" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to={tier.cta.to}
                className={`mt-5 ${buttonVariants({ variant: tier.highlight ? "default" : "outline" })}`}
              >
                {tier.cta.label}
              </Link>
            </section>
          ))}
        </div>

        <section className="mt-6 rounded-xl border border-white/12 bg-[rgb(8_11_15/0.75)] p-6 backdrop-blur-sm">
          <h2 className="font-semibold">Larger teams</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-300">
            More seats, more credit, priority queueing and API access. Tell us what you are testing
            and how often, and we will tell you what it costs.
          </p>
          <a href={`mailto:${brand.support_email}?subject=${brand.product_name} for a larger team`}
             className={`mt-4 ${buttonVariants({ variant: "outline", size: "sm" })}`}>
            Talk to us
          </a>
        </section>

        <section className="mt-10 grid gap-6 border-t border-white/10 pt-8 md:grid-cols-2">
          <div>
            <h3 className="font-semibold">What a run costs</h3>
            <p className="mt-1 text-sm text-slate-300">
              It depends on how many personas read your message and how many rounds they talk for.
              The price is worked out and shown before anything is spent, and you cannot confirm a
              run at a price that is not on the screen.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Mock runs are free</h3>
            <p className="mt-1 text-sm text-slate-300">
              Every plan can run the whole flow against a synthetic model without spending credit.
              It is how to learn the product, and how to check a study is set up the way you meant
              before paying for it.
            </p>
          </div>
        </section>

        <p className="mt-10 text-xs text-muted-foreground">
          Prices are in US dollars. {brand.product_name} is an experimental prototype: it simulates
          how AI personas react to a message, and is not a validated research instrument.
        </p>

        <footer className="mt-8 flex flex-wrap gap-4 border-t border-white/10 pt-6 text-xs text-slate-400">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/examples" className="hover:text-foreground">Examples</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </footer>
      </main>
    </div>
  );
}
